// @flow strict

import React from 'react';

import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

import { useLanguageContext } from '~contexts/language-context';
import { createLinkPath } from '~utils/helpers/create-link-path';

import type { Author } from '~types/author';

import * as styles from './author-preview.module.less';

/**
 * Type of component properties
 */
type AuthorPreviewProps = {
  author: Author,
  className?: string,
};

/**
 * Component to display author details.
 *
 * @param {AuthorPreviewProps} props - Component props.
 *
 * @returns {Node}
 */
export const AuthorPreview = ({ author, className }: AuthorPreviewProps) => {
  const { loc, language } = useLanguageContext();

  // if author is missing or not known
  if (author == null) {
    return <div>{loc('_author_no_author')}</div>;
  }

  const { name, image, position, path } = author;

  return (
    <div className={`${styles.author} ${className || ''}`}>
      <GatsbyImage
        image={image.src.childImageSharp.gatsbyImageData}
        className={`${styles.image} ${styles.preview}`}
        alt={name}
      />

      <div className={styles.info}>
        <p className={styles.name}>{name}</p>
        <p className={styles.position}>{position}</p>

        <Link
          className={styles.seeAll}
          to={createLinkPath({ path: `/blog/authors/${path}`, language })}
        >
          {loc('_author_read_all')}
        </Link>
      </div>
    </div>
  );
};
